import React, { useState } from 'react'
import { Button, ButtonText } from '../../components/ui/button'
import sidebar from '@/assets/svg/sidebar.svg'
import { ChevronDownIcon } from 'lucide-react'
import {
  ArrowLeftStartOnRectangleIcon,
  ArrowRightIcon,
  BugAntIcon,
  Cog6ToothIcon,
  EnvelopeIcon,
  LightBulbIcon,
  PlusCircleIcon,
  SquaresPlusIcon,
  StarIcon,
} from '@heroicons/react/16/solid'
import useAppStore from '@/store/useAppStore'
import useGetCurrentUser from '@/services/api/auth/useGetCurrentUser'
import useAuthStore from '@/store/useAuthStore'
import useGetTeams from '@/services/api/team/useGetTeams'
import useLogout from '@/services/api/auth/useLogout'
import { Link, useNavigate } from 'react-router-dom'
import useSwitchTeam from '@/services/api/team/useSwitchTeam'
import useSetDefaultTeam from '@/services/api/team/useSetDefaultTeam'
import EditTeam from './EditTeam'
import {
  Dialog,
  DialogBody,
  DialogClose,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { XMarkIcon } from '@heroicons/react/16/solid'

export interface SideBarProps {}

export default function SideBar() {
  const [openMenu, setOpenMenu] = React.useState(true)
  const [openBugReport, setOpenBugReport] = useState(false)
  const [openFeatureRequest, setOpenFeatureRequest] = useState(false)
  const { action: switchTeam, status: switchTeamStatus } = useSwitchTeam()

  const togglePanelState = useAppStore((state) => state.togglePanelState)
  const avatar = useAuthStore((state) => state.getAvatar())
  const currentTeamId = useAuthStore((state) => state.currentTeamId)
  const userData = useAuthStore((state) => state.userData)
  const { data: teams } = useGetTeams()

  const { data: currentUser } = useGetCurrentUser()
  const { logout } = useLogout()
  const { action: setDefaultTeam, status: defaultTeamStatus } =
    useSetDefaultTeam()

  const navigate = useNavigate()

  async function handleLogout() {
    logout()
  }

  async function handleSwitchTeam(id) {
    navigate('/')
    await switchTeam({ teamId: id })
  }

  const userEmail = currentUser?.email ?? ''
  const bugFormUrl = `https://forms.clickup.com/36879818/f/135fea-8516/I45S4ZP4LVM9B7WJ2M?Email=${encodeURIComponent(userEmail)}`
  const featureRequestFormUrl = `https://forms.clickup.com/36879818/f/135fea-8596/AW8OTUG6H8RRRX49RF?Email=${encodeURIComponent(userEmail)}`

  return (
    <>
      <div className="transition-width flex h-screen shrink-0 flex-col border bg-gray-100 duration-300">
        {/* Header */}
        <div className="flex items-center justify-between border p-3">
          <div className="flex items-center px-1">
            <Button
              variant="avatar"
              className="h-[24px] rounded-full py-4"
              onClick={togglePanelState}
            >
              <ButtonText>{avatar}</ButtonText>
            </Button>
            <p className="ml-2 text-txt-primary">{currentUser?.name}</p>
          </div>

          <Button variant="ghost" iconOnly onClick={togglePanelState}>
            <img src={sidebar} alt="sidebar" />
          </Button>
        </div>

        {/* My Spaces */}
        <div className="flex-1 overflow-auto p-4">
          <div className="flex items-center justify-between text-sm font-medium text-txt-secondary">
            <p>My Spaces</p>
            <div className="flex items-center">
              <Link to={'/create-space'}>
                <Button iconOnly variant="ghost">
                  <PlusCircleIcon />
                </Button>
              </Link>

              <Button iconOnly variant="ghost">
                <ChevronDownIcon onClick={() => setOpenMenu(!openMenu)} />
              </Button>
            </div>
          </div>
          {openMenu && (
            <ul className="">
              {teams?.map((team) => (
                <li
                  key={team.id}
                  data-loading={
                    [
                      defaultTeamStatus.status,
                      switchTeamStatus.status,
                    ].includes('pending')
                      ? true
                      : undefined
                  }
                  data-active={team.id === currentTeamId ? true : undefined}
                  className="group flex w-full items-center justify-between rounded-md p-2 text-sm font-medium text-txt-primary hover:bg-gray-tr-08 data-[loading]:pointer-events-none data-[loading]:animate-pulse data-[active]:bg-green-tr-12 data-[active]:text-txt-success"
                >
                  <div className="grid grid-cols-[1.25rem,1fr]">
                    <div className="flex items-center justify-start">
                      {team.is_default ? (
                        <div>
                          <StarIcon className="size-4 text-blue-500" />
                        </div>
                      ) : (
                        <div className="flex">
                          <Button
                            className="hidden h-max w-max p-0 group-hover:block"
                            iconOnly
                            variant="ghost"
                            title="Make default"
                            type="button"
                            onClick={() => setDefaultTeam({ teamId: team.id })}
                          >
                            <StarIcon className="size-4 text-blue-500" />
                          </Button>
                          <div className="ml-[1px] size-3 rounded-full bg-gray-300 group-hover:hidden"></div>
                        </div>
                      )}
                    </div>
                    <label
                      className="overflow-hidden text-ellipsis whitespace-nowrap"
                      title={team.name}
                    >
                      {team.name}
                    </label>
                  </div>

                  <div className="hidden items-center group-hover:flex">
                    <EditTeam teamId={team.id} />

                    {currentTeamId !== team.id && (
                      <Button
                        className="h-max w-max p-[2px]"
                        iconOnly
                        variant="ghost"
                        title="Switch space"
                        onClick={() => handleSwitchTeam(team.id)}
                      >
                        <ArrowRightIcon />
                      </Button>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Bottom Settings Section */}
        <div className="border-t pt-2 pb-3 px-2">
          <p className="px-2 py-1 text-xs font-medium text-txt-secondary uppercase tracking-wider">
            Settings
          </p>
          <nav className="flex flex-col gap-0.5">
            <Link
              to="/account"
              className="flex items-center gap-2 rounded-md px-2 py-1.5 text-sm text-txt-primary hover:bg-gray-tr-08"
            >
              <Cog6ToothIcon className="size-4 text-txt-secondary" />
              Settings
            </Link>
            <a
              href="https://www.profitflow.au/whats-new"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 rounded-md px-2 py-1.5 text-sm text-txt-primary hover:bg-gray-tr-08"
            >
              <SquaresPlusIcon className="size-4 text-txt-secondary" />
              What's New
            </a>
            <button
              onClick={() => setOpenBugReport(true)}
              className="flex items-center gap-2 rounded-md px-2 py-1.5 text-sm text-txt-primary hover:bg-gray-tr-08 text-left"
            >
              <BugAntIcon className="size-4 text-txt-secondary" />
              Report a Bug
            </button>
            <button
              onClick={() => setOpenFeatureRequest(true)}
              className="flex items-center gap-2 rounded-md px-2 py-1.5 text-sm text-txt-primary hover:bg-gray-tr-08 text-left"
            >
              <LightBulbIcon className="size-4 text-txt-secondary" />
              Feature Request
            </button>
            <a
              href="https://www.profitflow.au/home#anchors-m3racewc"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 rounded-md px-2 py-1.5 text-sm text-txt-primary hover:bg-gray-tr-08"
            >
              <EnvelopeIcon className="size-4 text-txt-secondary" />
              Contact Us
            </a>
            <button
              onClick={handleLogout}
              className="flex items-center gap-2 rounded-md px-2 py-1.5 text-sm text-txt-primary hover:bg-gray-tr-08 text-left"
            >
              <ArrowLeftStartOnRectangleIcon className="size-4 text-txt-secondary" />
              Log Out
            </button>
          </nav>
        </div>
      </div>

      {/* Submit Bug Dialog */}
      <Dialog open={openBugReport} onOpenChange={setOpenBugReport}>
        <DialogContent className="max-w-2xl h-[80vh] flex flex-col">
          <DialogHeader>
            <DialogTitle>Report a Bug</DialogTitle>
            <DialogClose>
              <XMarkIcon />
            </DialogClose>
          </DialogHeader>
          <DialogBody className="flex-1 overflow-hidden p-0">
            <iframe
              key={userEmail}
              src={bugFormUrl}
              className="h-full w-full border-0"
              title="Report a Bug"
            />
          </DialogBody>
        </DialogContent>
      </Dialog>

      {/* Feature Request Dialog */}
      <Dialog open={openFeatureRequest} onOpenChange={setOpenFeatureRequest}>
        <DialogContent className="max-w-2xl h-[80vh] flex flex-col">
          <DialogHeader>
            <DialogTitle>Feature Request</DialogTitle>
            <DialogClose>
              <XMarkIcon />
            </DialogClose>
          </DialogHeader>
          <DialogBody className="flex-1 overflow-hidden p-0">
            <iframe
              key={userEmail}
              src={featureRequestFormUrl}
              className="h-full w-full border-0"
              title="Feature Request"
            />
          </DialogBody>
        </DialogContent>
      </Dialog>
    </>
  )
}
